/**
 * GiftTransactionItem
 * Flutter source: wallet_screen.dart - _giftItem()
 *
 * Displays gift transactions (content, live, video call, chat gifts).
 */
import React from 'react';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ContentPreview } from '@/presentation/feature/content/components/ContentPreview';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { MathUtils } from '@/domain/utils/mathUtils';
import { GiftUtils } from '@/domain/utils/giftUtils';
import { env } from '@/config/env';
import { WalletUserAvatar } from './WalletUserAvatar';

interface GiftTransactionItemProps {
  transaction: AppTransactionEntity;
  currentUserId: string;
  onContentTap: (transaction: AppTransactionEntity) => void;
}

export const GiftTransactionItem: React.FC<GiftTransactionItemProps> = ({
  transaction,
  currentUserId,
  onContentTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const dateFormatters = useDateFormatters();

  const isCurrentUser = transaction.usersInfo.userId === currentUserId;
  const isContentGift = transaction.mediaInfo != null;
  const isLiveGift = transaction.broadcastingInfo != null;
  const isVideoCallGift = transaction.chatVideoCallInfo != null;

  let title: string;
  if (isCurrentUser) {
    const peerName = transaction.usersInfo.peerName ?? '';
    if (isContentGift) {
      title = `${l10n.wallet.youSent} ${peerName} ${l10n.wallet.contentGift}`;
    } else if (isLiveGift) {
      title = `${l10n.wallet.youSent} ${peerName} ${l10n.wallet.liveGift}`;
    } else if (isVideoCallGift) {
      title = `${l10n.wallet.youSent} ${peerName} ${l10n.wallet.videoCallGift}`;
    } else {
      title = `${l10n.wallet.youSent} ${peerName} ${l10n.wallet.chatGift}`;
    }
  } else {
    const senderName = transaction.usersInfo.name;
    const to = transaction.usersInfo.peerUserId === currentUserId ? l10n.wallet.you : (transaction.usersInfo.peerName ?? '');
    if (isContentGift) {
      title = `${senderName} ${l10n.wallet.sent} ${to} ${l10n.wallet.contentGift}`;
    } else if (isLiveGift) {
      title = `${senderName} ${l10n.wallet.sent} ${to} ${l10n.wallet.liveGift}`;
    } else if (isVideoCallGift) {
      title = `${senderName} ${l10n.wallet.sent} ${to} ${l10n.wallet.videoCallGift}`;
    } else {
      title = `${senderName} ${l10n.wallet.sent} ${to} ${l10n.wallet.chatGift}`;
    }
  }

  const avatarUserId = isCurrentUser
    ? (transaction.usersInfo.peerUserId ?? '')
    : transaction.usersInfo.userId;

  const thumbnailUrl = isContentGift && transaction.mediaInfo
    ? `${env.CDN_NAME}${transaction.mediaInfo.thumbnail}`
    : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.sm }}>
      {/* User avatar */}
      <WalletUserAvatar userId={avatarUserId} alt={transaction.usersInfo.name} />

      {/* Title + subtitle */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: theme.text.primary, fontSize: responsive.font.sm, fontWeight: responsive.fontWeight.medium }}>
          {title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.xs, marginTop: responsive.gap.xs }}>
          {transaction.giftInfo && (transaction.giftInfo.instances ?? 0) > 1 && (
            <span style={{ color: theme.text.secondary, fontSize: responsive.font.xs }}>
              {transaction.giftInfo.instances} x
            </span>
          )}
          {transaction.giftInfo && (
            <img
              src={GiftUtils.getGiftPath(transaction.giftInfo.thumbnail)}
              alt={transaction.giftInfo.name}
              style={{ width: responsive.icon.medium, height: responsive.icon.medium, objectFit: 'cover' }}
            />
          )}
          {transaction.giftInfo && (
            <span style={{ color: theme.text.secondary, fontSize: responsive.font.xs }}>
              - {MathUtils.removeTrailingZero(transaction.giftInfo.coins)}
            </span>
          )}
          <AssetIcon
            path="/assets/images/coins.png"
            width={responsive.icon.small}
            height={responsive.icon.small}
            color={theme.exclusive}
          />
          <span style={{ color: theme.text.secondary, fontSize: responsive.font.xs }}>
            · {dateFormatters.formatDate(transaction.createdDate, true)}
          </span>
        </div>
      </div>

      {/* Content thumbnail (if content gift) */}
      {isContentGift && thumbnailUrl && (
        <div
          style={{
            width: responsive.avatar.medium,
            height: responsive.avatar.medium,
            borderRadius: responsive.borderRadius.base,
            overflow: 'hidden',
            flexShrink: 0,
            cursor: 'pointer',
          }}
          onClick={() => onContentTap(transaction)}
        >
          <ContentPreview
            thumbnailUrl={thumbnailUrl}
            alt="Gift content"
          />
        </div>
      )}
    </div>
  );
};
